import { css, type SerializedStyles } from '@emotion/react'
import { type Theme } from '@mui/material/styles'
import { rem } from 'polished'

import { type InputPlaceOptions } from '@DS/components/navigation/search/common/ODSearch.types'
import { highContrast } from '@Styles/a11y'
import { Z_INDEX_HIGH, Z_INDEX_LOW } from '@Styles/constants/layout'
import { RADIUS_1 } from '@Styles/constants/radius'
import { resetFieldset } from '@Styles/form'

const inputPlaceAboveSeparatorOnFocus = (theme: Theme, hasError = false): SerializedStyles =>
  css({
    ':focus-within': {
      '& ~ hr': {
        zIndex: -1,
      },
      borderColor: hasError ? theme.palette.error.main : theme.palette.primary.main,
      boxShadow: `0 0 0 1px inset ${hasError ? theme.palette.error.main : theme.palette.primary.main}`,
    },
  })

export const inputPlace = (
  theme: Theme,
  { disabled = false, hasError = false, isSwapHidden = false, elevation }: InputPlaceOptions = {},
): SerializedStyles =>
  css(
    {
      ':hover': {
        backgroundColor: disabled ? theme.customPalette.background.paper : theme.customPalette.onElements.selected,
      },
      backgroundColor:
        elevation === 'onSurface' ? theme.customPalette.background.default : theme.customPalette.background.paper,
      border: `1px solid transparent`,
      ...(hasError && {
        '& ~ hr': {
          zIndex: -1,
        },
        borderColor: theme.palette.error.main,
      }),
      color: theme.palette.text.primary,
      height: rem(48),
      padding: isSwapHidden ? theme.spacing('0', 2) : theme.spacing('0', 9, '0', 2),
    },
    inputPlaceAboveSeparatorOnFocus(theme, hasError),
  )

export const odsearch = css({
  position: 'relative',
  zIndex: Z_INDEX_HIGH,
})

export const odsearchFieldset = css(resetFieldset, {
  display: 'flex',
  flexDirection: 'column',
})

export const originInputPlace = css({
  borderRadius: `${RADIUS_1} ${RADIUS_1} 0 0`,
})

export const destinationInputPlace = css({
  borderRadius: `0 0 ${RADIUS_1} ${RADIUS_1}`,
})

const absolute = css({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: Z_INDEX_LOW,
})

export const separator = css(
  highContrast({
    border: 'solid 1px transparent',
  }),
  {
    width: '100%',
  },
  absolute,
)

export const swap = (theme: Theme): SerializedStyles =>
  css(
    {
      right: theme.spacing(2),
    },
    absolute,
  )
